import type { CSSProperties, HTMLAttributes, PropsWithChildren } from 'react';
import { forwardRef, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { uuid } from '@elfsight/lib-utils';
import useMergedRef from '@react-hook/merged-ref';
import type { Rect } from 'react-use-rect';
import { useRect } from 'react-use-rect';
import { Portal } from '../portal';
import {
  acquireBodyRelativePosition,
  releaseBarOffset,
  releaseBodyRelativePosition,
  setBarOffset
} from '../utils';
import { zIndexLayer } from '../zIndexLayer';

type barMode = 'static' | 'floating-top' | 'floating-bottom';
interface BarProps extends PropsWithChildren, HTMLAttributes<HTMLDivElement> {
  mode?: barMode;
  portalElement?: HTMLDivElement;
  className?: string;
  style?: CSSProperties;
  shadow?: boolean;
}

export const Bar = forwardRef<HTMLDivElement, BarProps>(function (
  {
    children,
    mode = 'static',
    portalElement,
    className,
    style,
    shadow,
    ...forwardingProps
  },
  forwardRef
) {
  const [rect, setRect] = useState<Rect | null>(null);
  const [rectRef] = useRect(setRect, { resize: true });
  const offsetId = useRef('');
  if (!offsetId.current) {
    offsetId.current = `bar-offset-${uuid()}`;
  }

  useEffect(() => {
    acquireBodyRelativePosition();

    return releaseBodyRelativePosition;
  }, []);

  useEffect(() => {
    const currentId = offsetId.current;
    const currentHeight = rect?.height ?? 0;
    const offsetSide = mode === 'floating-bottom' ? 'bottom' : 'top';

    setBarOffset({
      id: currentId,
      height: currentHeight,
      side: offsetSide
    });

    return () => {
      releaseBarOffset(currentId);
    };
  }, [rect?.height, mode]);

  const mergedRef = useMergedRef(rectRef, forwardRef);

  return (
    <Portal containerElement={portalElement} shadow={shadow}>
      <BarContainer
        {...forwardingProps}
        ref={mergedRef}
        style={style}
        className={className}
        _mode={mode}
        _offset={rect?.height}
      >
        {children}
      </BarContainer>
    </Portal>
  );
});

const BarContainer = styled.div<{
  _position?: string;
  _offset?: number;
  _mode?: barMode;
}>`
  z-index: ${zIndexLayer.modal};

  width: 100%;
  height: max-content;
  left: 0;

  position: ${({ _mode = 'static' }) => cssPositions[_mode]};
  top: ${({ _offset = 0, _mode = 'static' }) => {
    if (_mode === 'static') return `${-_offset}px`;
    if (_mode === 'floating-top') return 0;
    return null;
  }};

  bottom: ${({ _mode = 'static' }) => {
    if (_mode === 'floating-bottom') return 0;
    return null;
  }};
`;

const cssPositions = {
  'floating-bottom': 'fixed',
  'floating-top': 'fixed',
  static: 'absolute'
};
